import type { InputProps } from '@chakra-ui/react'
import { Box, Field, Input, useControllableState } from '@chakra-ui/react'
import { useEffect, useMemo, useRef } from 'react'

type VerificationCodeInputProps = Omit<
  InputProps,
  'value' | 'defaultValue' | 'onChange'
> & {
  length?: number
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  numeric?: boolean

  /** Called once when all cells are filled */
  handleVerification: () => void
}

export function VerificationCodeInput(props: VerificationCodeInputProps) {
  const {
    length = 6,
    value,
    defaultValue = '',
    onValueChange,
    numeric = true,
    handleVerification,
    ...inputProps
  } = props

  const [code, setCode] = useControllableState({
    value,
    defaultValue,
    onChange: onValueChange
  })

  // Normalize to exactly `length` chars for rendering
  const chars = useMemo(() => {
    const raw = (code ?? '').slice(0, length)
    return Array.from({ length }, (_, i) => raw[i] ?? '')
  }, [code, length])

  const refs = useRef<Array<HTMLInputElement | null>>([])
  const focusIndex = (i: number) => refs.current[i]?.focus()
  const selectIndex = (i: number) => refs.current[i]?.select()

  const sanitize = (s: string) => {
    const trimmed = s.replace(/\s+/g, '')
    return numeric ? trimmed.replace(/\D+/g, '') : trimmed
  }

  const setAt = (index: number, nextChar: string) => {
    const next = chars.slice()
    next[index] = nextChar
    setCode(next.join(''))
  }

  const fillFrom = (startIndex: number, text: string) => {
    const next = chars.slice()
    const clean = sanitize(text).slice(0, length - startIndex)
    for (let i = 0; i < clean.length; i++) {
      next[startIndex + i] = clean[i]
    }
    setCode(next.join(''))

    const nextFocus = Math.min(startIndex + clean.length, length - 1)
    focusIndex(nextFocus)
    selectIndex(nextFocus)
  }

  const handleChange = (index: number, rawValue: string) => {
    const clean = sanitize(rawValue)

    if (clean.length <= 1) {
      setAt(index, clean)
      if (clean && index < length - 1) focusIndex(index + 1)
      return
    }

    fillFrom(index, clean)
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    switch (e.key) {
      case 'Backspace': {
        if (chars[index]) {
          e.preventDefault()
          setAt(index, '')
          return
        }
        if (index > 0) {
          e.preventDefault()
          setAt(index - 1, '')
          focusIndex(index - 1)
        }
        return
      }
      case 'ArrowLeft': {
        if (index > 0) {
          e.preventDefault()
          focusIndex(index - 1)
          selectIndex(index - 1)
        }
        return
      }
      case 'ArrowRight': {
        if (index < length - 1) {
          e.preventDefault()
          focusIndex(index + 1)
          selectIndex(index + 1)
        }
        return
      }
      default:
        return
    }
  }

  const handlePaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = e.clipboardData.getData('text')
    const clean = sanitize(text)
    if (!clean) return
    e.preventDefault()
    fillFrom(index, clean)
  }

  // --- Call handleVerification when complete (once per completed value) ---
  const lastVerifiedRef = useRef<string>('')

  useEffect(() => {
    if (!handleVerification) return

    const normalized = (code ?? '').slice(0, length)
    const isComplete =
      normalized.length === length &&
      !normalized.split('').some((c) => c === '')

    if (!isComplete) {
      // Allow verifying again after user edits
      lastVerifiedRef.current = ''
      return
    }

    if (lastVerifiedRef.current === normalized) return
    lastVerifiedRef.current = normalized
    handleVerification()
  }, [code, length, handleVerification])

  return (
    <Field.Root w="auto">
      <Box display="flex" gap="10px" justifyContent="center">
        {chars.map((ch, i) => (
          <Input
            key={i}
            ref={(el) => {
              refs.current[i] = el
            }}
            inputMode={numeric ? 'numeric' : inputProps.inputMode}
            autoComplete={numeric ? 'one-time-code' : inputProps.autoComplete}
            type={numeric ? 'text' : (inputProps.type ?? 'text')}
            value={ch}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={(e) => handlePaste(i, e)}
            onFocus={(e) => {
              inputProps.onFocus?.(e)
              requestAnimationFrame(() => e.currentTarget?.select())
            }}
            textAlign="center"
            fontSize="28px"
            lineHeight="36px"
            fontWeight="400"
            w="56px"
            h="72px"
            maxLength={length}
            borderRadius="10px"
            border="1px solid"
            borderColor="border.subtle"
            bg="bg.surface"
            color="text.primary"
            transition="all 0.2s ease"
            _focus={{
              borderColor: 'accent.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
              outline: 'none'
            }}
            _hover={{
              borderColor: 'border.strong'
            }}
            {...inputProps}
          />
        ))}
      </Box>
    </Field.Root>
  )
}
